import type { AppPlugin } from '../Plugin';
import type { KeepAlive } from '../Def';
import { AppState } from '../App';

declare module '../Def' {
    interface AppPluginOptions {
        keepAlive?: KeepAlive;
    }

    interface LifecycleFns<ExtraProps> {
        suspend?: LifecycleFn<ExtraProps> | Array<LifecycleFn<ExtraProps>>;
        resume?: LifecycleFn<ExtraProps> | Array<LifecycleFn<ExtraProps>>;
    }
}

function getRootElement(rootDOM: Element | string): HTMLElement {
    let root: HTMLElement | null;
    if ('string' === typeof rootDOM) {
        root = document.querySelector(rootDOM) as HTMLElement;
        if (!root) {
            throw Error(`Cannot find rootDOM ${rootDOM}.`);
        }
    } else if (rootDOM instanceof HTMLElement) {
        root = rootDOM;
    } else {
        throw Error(`The rootDOM need to be a selector or an element.`);
    }

    return root;
}

const parseKeepAlive = (keepAlive?: KeepAlive): Exclude<KeepAlive, boolean> | undefined => {
    switch (keepAlive) {
        case true:
            return {};
        case false:
        case null:
        case undefined:
            return undefined;
    }

    let useHiddenClass = '';

    if ('string' === typeof keepAlive.useHiddenClass) {
        useHiddenClass =
            'undefined' === typeof CSS ? keepAlive.useHiddenClass.trim() : CSS.escape(keepAlive.useHiddenClass.trim());
    } else {
        if ('undefined' !== typeof keepAlive.useHiddenClass) {
            console.warn(`useHiddenClass can only be a string, but it's ${useHiddenClass}.`);
        }
        useHiddenClass = '';
    }

    let useHiddenAttribute = keepAlive.useHiddenAttribute;

    if ('boolean' !== typeof useHiddenAttribute && 'undefined' !== typeof useHiddenAttribute) {
        console.warn(`useHiddenAttribute can only be a boolean, but it's ${useHiddenAttribute}.`);
        useHiddenAttribute = false;
    }

    let detachDOM = keepAlive.detachDOM;

    if ('boolean' !== typeof detachDOM && 'undefined' !== typeof detachDOM) {
        console.warn(`detachDOM can only be a boolean, but it's ${detachDOM}.`);
        detachDOM = false;
    }

    return {
        ...keepAlive,
        useHiddenClass,
        useHiddenAttribute,
        detachDOM,
    };
};

export function createAttachDOMPlugin<AdditionalOptions, CustomProps>(
    rootDOM: Element | string
): AppPlugin<AdditionalOptions, CustomProps> {
    return ({ app, debug }) => {
        let everMounted = false;
        const wrapperCommentPlaceholder: Comment = document.createComment(` placeholder of ${app.name} `);

        const resolveRootDOM = ((): (() => HTMLElement) => {
            let rootElement: HTMLElement | null = null;
            return (): HTMLElement => {
                if (rootElement) {
                    return rootElement;
                }

                rootElement = getRootElement(rootDOM);
                return rootElement;
            };
        })();

        app.lifecycle.hooks.aftermount.tap('AttachDOMPlugin', () => {
            everMounted = true;
        });

        const keepAliveOption = parseKeepAlive(app.options.keepAlive);

        if (keepAliveOption) {
            app.lifecycle.hooks.mount.tapPromise('AttachDOMPlugin', () => {
                if (everMounted) {
                    debug('Call resume instead of mount in keepAlive mode.');
                    return app.lifecycle.fns.call('resume', app.lifecycle.customProps, true).then(() => true);
                }
                return Promise.resolve();
            });

            app.lifecycle.hooks.unmount.tapPromise('AttachDOMPlugin', () => {
                if (app.state === AppState.UNLOADING) {
                    return Promise.resolve();
                }
                debug('Call suspend instead of unmount in keepAlive mode.');
                return app.lifecycle.fns.call('suspend', app.lifecycle.customProps, true).then(() => true);
            });
        }

        let preDisplay = '';

        app.lifecycle.hooks.beforemount.tap('AttachDOMPlugin', () => {
            if (keepAliveOption && everMounted && !keepAliveOption.detachDOM) {
                debug("Don't need to attach DOM if ever mounted in keepAlive mode, instead we show it.");
                const { useHiddenAttribute, useHiddenClass } = keepAliveOption;

                if (useHiddenAttribute) {
                    app.wrapperDOM.removeAttribute('hidden');
                }

                if (useHiddenClass) {
                    app.wrapperDOM.classList.remove(useHiddenClass);
                }

                if (!useHiddenAttribute && !useHiddenClass) {
                    app.wrapperDOM.style.display = preDisplay;
                }

                return;
            }

            const rootElement = resolveRootDOM();
            debug('Attach wrapperDOM(%o) to rootDOM(%o).', app.wrapperDOM, rootElement);

            if (wrapperCommentPlaceholder.parentElement === rootElement) {
                rootElement.replaceChild(app.wrapperDOM, wrapperCommentPlaceholder);
            } else {
                rootElement.appendChild(app.wrapperDOM);
            }
        });

        app.lifecycle.hooks.afterunmount.tap('AttachDOMPlugin', () => {
            if (keepAliveOption && app.state !== AppState.UNLOADING && !keepAliveOption.detachDOM) {
                debug("Don't detach DOM in keepAlive mode, instead we hide it.");
                const { useHiddenAttribute, useHiddenClass } = keepAliveOption;

                if (useHiddenAttribute) {
                    app.wrapperDOM.setAttribute('hidden', 'hidden');
                }

                if (useHiddenClass) {
                    app.wrapperDOM.classList.add(useHiddenClass);
                }

                if (!useHiddenAttribute && !useHiddenClass) {
                    preDisplay = app.wrapperDOM.style.display;
                    app.wrapperDOM.style.display = 'none';
                }

                return;
            }

            const rootElement = resolveRootDOM();
            debug('Detach wrapperDOM(%o) from rootDOM(%o).', app.wrapperDOM, rootElement);

            if (app.wrapperDOM.parentElement === rootElement) {
                rootElement.replaceChild(wrapperCommentPlaceholder, app.wrapperDOM);
            } else {
                app.wrapperDOM.parentElement?.removeChild(app.wrapperDOM);
            }

            everMounted = false;
        });

        app.hooks.afterunload.tap('AttachDOMPlugin', () => {
            wrapperCommentPlaceholder.parentElement?.removeChild(wrapperCommentPlaceholder);
        });
    };
}
